import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {
	Button,
	FormGroup,
	Form,
	Row,
	Col,
	Stack,
} from "react-bootstrap";
import { Formik } from "formik";
import { toast } from "react-toastify";
import API from "../../api";
import {Helmet} from "react-helmet";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GenerateOfferLink = () => {
	const navigate = useNavigate();
	const [offer, setOffer] = useState(null);

	const notify = (message) =>
		toast.error(message, {
			position: toast.POSITION.TOP_RIGHT,
		});

	const success = (message) =>
		toast.success(message, {
			position: toast.POSITION.TOP_RIGHT,
		});

	const copyLink = () => {
		navigator.clipboard.writeText(offer.url);
		success("Link copied");
	};

	return (
		<Formik
			initialValues={{
				email: "",
				note: "",
				durationHours: 72,
				type: "personal",
			}}
			validate={(values) => {
				const errors = {};
				if (values.email && !EMAIL_RE.test(values.email)) {
					errors.email = "Enter a valid email address";
				}
				if (!values.durationHours || Number(values.durationHours) < 1) {
					errors.durationHours = "Enter a positive number of hours";
				}
				return errors;
			}}
			onSubmit={(values, { setSubmitting }) => {
				API.post("/admin/discount-offer", {
					email: values.email || undefined,
					note: values.note || undefined,
					durationHours: Number(values.durationHours) || 72,
					// Personal = single use; Public = unlimited (no cap).
					maxRedemptions: values.type === "public" ? null : 1,
				})
					.then((res) => {
						setSubmitting(false);
						setOffer(res.data);
						success("Offer link generated");
					})
					.catch((error) => {
						setSubmitting(false);
						if (error.response) {
							notify(error.response.data.message);
						}
					});
			}}
		>
			{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					isSubmitting,
				}) => (
				<>
					<Helmet>
						<title>Generate Offer Link | MyGolfDNA.com</title>
						<meta name="description" content="Generate a manual discount offer link"/>
					</Helmet>
					<Row>
						<Col md={8}>
							<Form noValidate className="mt-5 form-group" onSubmit={handleSubmit}>
								<Stack direction="horizontal" className="mb-2">
									<h3 className="mb-0">Generate Offer Link</h3>
									<Button
										variant="link"
										className="ms-auto"
										onClick={() => navigate("/admin/discount-offers")}
									>
										← Offer Links
									</Button>
								</Stack>
								<p className="text-black-50">
									Creates a link to a discounted plan (one that has "Requires
									Offer" set to Yes). A <b>Personal</b> link can be redeemed once;
									a <b>Public</b> link can be redeemed by anyone until it expires.
									Works for people without an account too.{" "}
									<b>Sharing one link with many people</b> (like a comment on
									every review)? Choose Public — a Personal link stops working
									after the first purchase.
								</p>
								<Form.Group className="form-floating mb-3">
									<Form.Select
										name="type"
										id="type"
										value={values.type}
										onChange={handleChange}
									>
										<option value="personal">Personal — single use</option>
										<option value="public">Public — unlimited until it expires</option>
									</Form.Select>
									<Form.Label htmlFor="type">Link type</Form.Label>
								</Form.Group>
								<Form.Group className="form-floating mb-3">
									<Form.Control
										placeholder="Email (optional)"
										type="email"
										name="email"
										id="email"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.email}
										isInvalid={touched.email && !!errors.email}
									/>
									<Form.Label htmlFor="email">Email (optional — who it's for)</Form.Label>
									<Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
								</Form.Group>
								<Form.Group className="form-floating mb-3">
									<Form.Control
										placeholder="Note (optional)"
										as="textarea"
										name="note"
										id="note"
										style={{height: "80px"}}
										onChange={handleChange}
										value={values.note}
									/>
									<Form.Label htmlFor="note">Note (optional — for your reference)</Form.Label>
								</Form.Group>
								<Form.Group className="form-floating mb-3">
									<Form.Control
										placeholder="Duration in hours"
										type="number"
										name="durationHours"
										id="durationHours"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.durationHours}
										isInvalid={touched.durationHours && !!errors.durationHours}
									/>
									<Form.Label htmlFor="durationHours">Expires after (hours)</Form.Label>
									<Form.Control.Feedback type="invalid">{errors.durationHours}</Form.Control.Feedback>
								</Form.Group>
								<FormGroup className="mt-4">
									<Stack direction="horizontal" gap={3}>
										<Button variant="primary" size="lg" type="submit" disabled={isSubmitting}>
											{isSubmitting ? (
												<span className="spinner-border spinner-border-sm" role="status" aria-hidden={true} />
											) : (
												"Generate Link"
											)}
										</Button>
									</Stack>
								</FormGroup>
							</Form>
							{offer && (
								<div className="mt-4">
									<Form.Group className="form-floating mb-3">
										<Form.Control readOnly value={offer.url} id="offerUrl" />
										<Form.Label htmlFor="offerUrl">Offer link</Form.Label>
									</Form.Group>
									<Stack direction="horizontal" gap={3}>
										<Button variant="outline-primary" onClick={copyLink}>Copy Link</Button>
										<Button variant="link" onClick={() => navigate("/admin/discount-offers")}>
											View all offer links
										</Button>
										<span className="text-black-50 ms-auto">
											Expires {new Date(offer.expiresAt).toLocaleString()}
										</span>
									</Stack>
								</div>
							)}
						</Col>
					</Row>
				</>
			)}
		</Formik>
	);
};

export default GenerateOfferLink;
